import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Dialog, DialogContent,
  TextField, Grid, MenuItem, Checkbox, FormControlLabel,
  CircularProgress, IconButton, Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import { makeStyles } from "@material-ui/core/styles";
import Slider from "@material-ui/core/Slider";
import Cropper from "react-easy-crop";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./CampaignForm.scss";
import Select from "react-select";
import Button from "components/button/Button";
import InfoTooltip from "components/InfoTooltip";
import { LabelField } from "components/LabelField";
import FinanceService from "Lib/API/Finance";
import getCroppedImg from "components/CropImage";
import { uploadFile } from "pages/Auth/Presigned";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDateFormatForDatePicket, getDateFormat, formatDate } from "helper/dateFormat";
import { createCampaign, updateCampaign } from "./fundraisingSlice";
import { toastr } from "react-redux-toastr";
import { BANNER_WIDTH, BANNER_HEIGHT, BANNER_ASPECT, BANNER_SIZE_LABEL, bannerCssVars } from "./campaignBanner";
import { fontConfig } from "theme/fontConfig";

// ── WYSIWYG editor ─────────────────────────────────────────────────────────
const CampaignDescriptionEditor = ({ initialHtml, onChange }) => {
  const [editorState, setEditorState] = useState(() => {
    if (!initialHtml) return EditorState.createEmpty();
    const block = htmlToDraft(initialHtml);
    if (!block) return EditorState.createEmpty();
    return EditorState.createWithContent(
      ContentState.createFromBlockArray(block.contentBlocks)
    );
  });

  const handleChange = (state) => {
    setEditorState(state);
    onChange(draftToHtml(convertToRaw(state.getCurrentContent())));
  };

  return (
    <div className="campaign-wysiwyg">
      <Editor
        editorState={editorState}
        onEditorStateChange={handleChange}
        toolbar={{
          options: ["inline", "blockType", "list", "link", "remove"],
          inline: { options: ["bold", "italic", "underline"] },
          blockType: { options: ["Normal", "H2", "H3", "Blockquote"] },
          list: { options: ["unordered", "ordered"] },
        }}
      />
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────
const adjustColor = (hex, amount) => {
  const clamp = v => Math.min(255, Math.max(0, v));
  const n = parseInt(hex.replace('#', ''), 16);
  const r = clamp(((n >> 16) & 0xff) + amount);
  const g = clamp(((n >> 8) & 0xff) + amount);
  const b = clamp((n & 0xff) + amount);
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
};

const bannerGradient = color => {
  const c = color || '#2455ef';
  return `linear-gradient(135deg, ${adjustColor(c, -30)} 0%, ${c} 60%, ${adjustColor(c, 30)} 100%)`;
};

const selectMenuStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "#2684ffb8" : "#F3F6F9",
    color: "#333",
    cursor: "pointer",
  }),
};

const scrollSelectIntoView = e => {
  const el = (e?.target || document.activeElement)?.closest?.(".react-select__control");
  if (!el) return;
  el.scrollIntoView({ block: "nearest" });
  const scroller = el.closest(".MuiDialogContent-root");
  if (scroller) scroller.scrollTop += 2;
};

const inputStyle = {
  backgroundColor: "#F3F6F9",
  borderColor: "#F3F6F9",
  borderRadius: 6,
  fontFamily: fontConfig.FundraisingInputFont.family,
  fontSize: 14,
};

const useStyles = makeStyles(() => ({
  dialogTitle: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 24px",
    fontFamily: fontConfig.FundraisingDialogTitleFont.family, fontSize: 18, fontWeight: 700, color: "#002E5C",
    borderBottom: "1px solid #f3f6f9", flexShrink: 0,
  },
  dialogContent: {
    padding: "0", overflowX: "hidden", overflowY: "auto", flex: 1,
  },
  dialogFooter: {
    display: "flex", justifyContent: "flex-end", gap: 12,
    padding: "14px 24px 18px", borderTop: "1px solid #f3f6f9", flexShrink: 0,
  },
  sectionLabel: {
    fontFamily: fontConfig.FundraisingSectionLabelFont.family, fontSize: 11, fontWeight: 600,
    color: "#002E5C", opacity: 0.6, textTransform: "uppercase",
    letterSpacing: "0.06em", marginBottom: 8, display: "block",
  },
  sectionBlock: {
    background: "#f3f6f9", borderRadius: 8, padding: "16px 20px",
  },
  cropContainer: {
    position: "relative", width: "100%", aspectRatio: `${BANNER_WIDTH} / ${BANNER_HEIGHT}`,
    background: "#000", overflow: "hidden", margin: "0 auto", borderRadius: 8,
    "& .reactEasyCrop_CropArea": { width: "100% !important", height: "100% !important" },
  },
  zoomSlider: { padding: "12px 16px 0" },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 8 },
  amountChip: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 12px", background: "#fff", borderRadius: 20,
    border: "1px solid #2555EF", color: "#2555EF",
    fontFamily: fontConfig.FundraisingAmountChipFont.family, fontSize: 13, fontWeight: 600,
  },
  removeChip: { cursor: "pointer", lineHeight: 1, color: "#2555EF", marginLeft: 4, fontSize: 16 },
}));

const SysTextField = (props) => (
  <TextField
    variant="outlined" size="small" fullWidth
    InputProps={{ style: inputStyle }}
    InputLabelProps={{ style: { fontFamily: fontConfig.FundraisingTextFieldFont.family, fontSize: 13, color: "#002E5C", opacity: 0.7 } }}
    {...props}
  />
);

const DateInput = React.forwardRef((props, ref) => (
  <SysTextField {...props} inputRef={ref} InputLabelProps={{ shrink: true, style: { fontFamily: fontConfig.FundraisingSysTextFieldFont.family, fontSize: 13, color: "#002E5C", opacity: 0.7 } }} />
));

const TITLE_MAX = 100;
const SUBTITLE_MAX = 150;

const schema = Yup.object({
  title: Yup.string().required("Title is required").max(TITLE_MAX, `Title cannot exceed ${TITLE_MAX} characters`),
  sub_title: Yup.string().max(SUBTITLE_MAX, `Subtitle cannot exceed ${SUBTITLE_MAX} characters`),
  goal_amount: Yup.number().min(1, "Goal must be at least $1").required("Goal is required"),
  slug: Yup.string().matches(/^[a-z0-9-]*$/, "Lowercase letters, numbers and hyphens only"),
  end_date: Yup.date().nullable(),
  minimum_amount: Yup.number().min(1).nullable(),
});

const CampaignForm = ({ open, onClose, campaign, onSaved }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEdit = !!campaign;
  const fileInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const cropBoxRef = useRef(null);

  const orgLogo = useSelector(state => state.homeReducer?.headerLogo) || null;

  const [activeTab, setActiveTab] = useState("design");
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);

  const [ledgers, setLedgers] = useState([]);
  const [ledgersLoaded, setLedgersLoaded] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropSize, setCropSize] = useState({ width: 0, height: 0 });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newAmount, setNewAmount] = useState("");

  React.useEffect(() => {
    if (!open || ledgersLoaded) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await FinanceService.getLedgerSubLedgerList(token);
        if (res?.ledgers) setLedgers(res.ledgers);
      } finally { setLedgersLoaded(true); }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset editing state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setActiveTab("design");
      setEditingDescription(false);
      setEditingTitle(false);
      setNewAmount("");
      setRawImage(null);
      setCropperOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!cropperOpen) return undefined;
    const el = cropBoxRef.current;
    if (!el) return undefined;
    const sync = () => setCropSize({ width: el.clientWidth, height: el.clientHeight });
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cropperOpen]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ x: 0, y: 0 }); setZoom(1);
      setRawImage(reader.result); setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSaveCrop = async (setFieldValue) => {
    try {
      setUploadingImage(true);
      const blob = await getCroppedImg(rawImage, croppedAreaPixels);
      const token = await getAccessTokenSilently();
      const file = new File([blob], `campaign-cover-${Date.now()}.jpg`, { type: "image/jpeg" });
      const result = await uploadFile(file, file, token);
      setFieldValue("cover_image_url", result.link);
    } catch (err) { console.error("Image upload failed", err); }
    finally { setUploadingImage(false); setCropperOpen(false); }
  };

  const initialValues = {
    title:              campaign?.title || "",
    sub_title:          campaign?.sub_title || "",
    slug:               campaign?.slug || "",
    description:        campaign?.description || "",
    goal_amount:        campaign?.goal_amount || "",
    end_date:           campaign?.end_date ? campaign.end_date.slice(0, 10) : "",
    status:             campaign?.status || "draft",
    cover_image_url:    campaign?.cover_image_url || "",
    banner_color:       campaign?.banner_color || null,
    show_banner:        campaign?.show_banner !== false,
    show_org_logo:      campaign?.show_org_logo !== false,
    show_donor_wall:    campaign?.show_donor_wall !== false,
    allow_anonymous:    campaign?.allow_anonymous !== false,
    allow_bill_account: campaign?.allow_bill_account === true,
    show_countdown:     campaign?.show_countdown !== false,
    checkout_style:     campaign?.checkout_style || "single",
    ledger:             campaign?.ledger || { id: "", name: "" },
    sub_ledger:         campaign?.sub_ledger || { id: "", name: "" },
    preset_amounts:     campaign?.preset_amounts?.length ? campaign.preset_amounts : [18, 36, 54, 100, 180, 360],
    allow_open_amount:  campaign?.allow_open_amount !== false,
    minimum_amount:     campaign?.minimum_amount || 1,
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const token = await getAccessTokenSilently();
      const data = {
        ...values,
        cover_image_url: values.cover_image_url || "",
        ledger:     values.ledger?.id     ? values.ledger     : undefined,
        sub_ledger: values.sub_ledger?.id ? values.sub_ledger : undefined,
      };
      if (isEdit) {
        await dispatch(updateCampaign({ id: campaign._id, data, token }));
      } else {
        await dispatch(createCampaign({ data, token }));
      }
      if (onSaved) onSaved();
      resetForm();
      onClose();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  const SETTINGS_FIELDS = ["goal_amount", "slug", "minimum_amount"];

  const handleClickSubmit = async (validateForm, submitForm, setFieldTouched) => {
    const errors = await validateForm();
    if (!Object.keys(errors).length) {
      submitForm();
      return;
    }
    Object.keys(errors).forEach(k => setFieldTouched(k, true, false));
    if (SETTINGS_FIELDS.some(f => errors[f])) {
      setActiveTab("settings");
      toastr.warning("Required fields missing", "Please complete all required fields in the Settings tab");
    } else {
      toastr.warning("Required fields missing", "Please fill in all required fields before saving");
    }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ style: { maxHeight: "92vh", display: "flex", flexDirection: "column" } }}
    >
      <Formik key={String(open)} initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit} enableReinitialize>
        {({ values, errors, touched, isSubmitting, setFieldValue, validateForm, submitForm, setFieldTouched }) => (
          <Form
            style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
            onKeyDown={e => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault(); }}
          >

            {/* ── Header with tabs ── */}
            <div className={classes.dialogTitle}>
              <span style={{ fontSize: 16 }}>{isEdit ? "Edit Campaign" : "New Fundraising Campaign"}</span>

              {!cropperOpen && (
              <div className="cf-tabs">
                <button
                  type="button"
                  className={`cf-tab${activeTab === "design" ? " cf-tab--active" : ""}`}
                  onClick={() => { setActiveTab("design"); }}
                >
                  Design
                </button>
                <button
                  type="button"
                  className={`cf-tab${activeTab === "settings" ? " cf-tab--active" : ""}`}
                  onClick={() => { setActiveTab("settings"); setEditingDescription(false); setEditingTitle(false); }}
                >
                  Settings
                </button>
              </div>
              )}

              <IconButton size="small" onClick={onClose}>
                <CloseIcon style={{ color: "#002E5C" }} />
              </IconButton>
            </div>

            <DialogContent className={classes.dialogContent}>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />

              {/* ── Cropper overlay (shown over any tab) ── */}
              {cropperOpen ? (
                <div style={{ padding: "24px" }}>
                  <div ref={cropBoxRef} className={classes.cropContainer}>
                    {cropSize.width > 0 && (
                    <Cropper
                      image={rawImage} crop={crop} zoom={zoom}
                      aspect={BANNER_ASPECT} cropSize={cropSize}
                      objectFit="contain" showGrid={false} minZoom={1} maxZoom={8} restrictPosition={false}
                      onCropChange={setCrop} onZoomChange={setZoom}
                      onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                    />
                    )}
                  </div>
                  <div className={classes.zoomSlider}>
                    <span className={classes.sectionLabel}>Zoom</span>
                    <Slider value={zoom} min={1} max={8} step={0.05} onChange={(_, v) => setZoom(v)} />
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
                    <Button variant="primary-outline" size="sm" onClick={() => setCropperOpen(false)}>Cancel</Button>
                    <Button variant="primary" size="sm" disabled={uploadingImage} onClick={() => handleSaveCrop(setFieldValue)}>
                      {uploadingImage && <CircularProgress size={14} style={{ marginRight: 6, color: "#fff" }} />}
                      {uploadingImage ? "Uploading…" : "Apply Crop"}
                    </Button>
                  </div>
                </div>

              ) : activeTab === "design" ? (

                /* ══════════════════ DESIGN TAB ══════════════════ */
                <div className="cf-preview" style={bannerCssVars}>

                  {/* No-banner chips — visible only when banner is hidden */}
                  {!values.show_banner && (
                    <div className="cf-preview__no-banner-chips">
                      <button type="button" className="cf-preview__chip cf-preview__chip--off" onClick={() => setFieldValue("show_banner", true)}>
                        <span className="cf-preview__chip-plus">+</span> Banner
                      </button>
                      {values.show_org_logo ? (
                        <span className="cf-preview__chip cf-preview__chip--on">
                          <LogoIcon /> Logo
                          <button type="button" className="cf-preview__chip-x" onClick={() => setFieldValue("show_org_logo", false)}>✕</button>
                        </span>
                      ) : (
                        <button type="button" className="cf-preview__chip cf-preview__chip--off" onClick={() => setFieldValue("show_org_logo", true)}>
                          <span className="cf-preview__chip-plus">+</span> Logo
                        </button>
                      )}
                    </div>
                  )}

                  {/* Banner — shown only when show_banner is on */}
                  {values.show_banner && (
                    <div
                      className={`cf-preview__hero${values.banner_color && !values.cover_image_url ? " cf-preview__hero--with-title" : ""}`}
                      style={values.cover_image_url ? {} : { background: bannerGradient(values.banner_color) }}
                      onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
                    >
                      {values.cover_image_url ? (
                        <div
                          className="cf-preview__hero-bg"
                          style={{ backgroundImage: `url(${values.cover_image_url})` }}
                        />
                      ) : (
                        <div className="cf-preview__hero-empty">
                          {uploadingImage
                            ? <CircularProgress size={28} style={{ color: "#fff" }} />
                            : <>
                                <PhotoCameraIcon style={{ fontSize: 36 }} />
                                <span>Click to upload banner image</span>
                                <span style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>Recommended: {BANNER_SIZE_LABEL} px</span>
                              </>
                          }
                        </div>
                      )}
                      <div className="cf-preview__hero-change">
                        <PhotoCameraIcon style={{ fontSize: 28 }} />
                        <span>Change image</span>
                        <span style={{ fontSize: 11, opacity: 0.7 }}>{BANNER_SIZE_LABEL} px recommended</span>
                      </div>

                      <div className="cf-preview__hero-chips" onClick={e => e.stopPropagation()}>
                        <span className="cf-preview__color-chip" onClick={() => { if (colorInputRef.current) colorInputRef.current.click(); }}>
                          <span className="cf-preview__color-dot" style={{ background: values.banner_color || '#2455ef' }} />
                          Color
                          <input
                            ref={colorInputRef}
                            type="color"
                            value={values.banner_color || '#2455ef'}
                            onChange={e => setFieldValue("banner_color", e.target.value)}
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                          />
                          {values.banner_color && (
                            <button type="button" className="cf-preview__chip-x cf-preview__chip-x--light" onClick={e => { e.stopPropagation(); setFieldValue("banner_color", null); }}>✕</button>
                          )}
                        </span>

                        <span className="cf-preview__banner-chip" onClick={() => setFieldValue("show_banner", false)}>
                          <PhotoCameraIcon /> Hide Banner
                        </span>
                      </div>

                      {values.cover_image_url && (
                        <div className="cf-preview__image-chips" onClick={e => e.stopPropagation()}>
                          {rawImage && (
                            <button
                              type="button"
                              className="cf-preview__image-chip"
                              onClick={() => setCropperOpen(true)}
                            >
                              Resize Image
                            </button>
                          )}
                          <button
                            type="button"
                            className="cf-preview__image-chip"
                            onClick={() => setFieldValue("cover_image_url", "")}
                          >
                            Remove Image
                          </button>
                        </div>
                      )}

                      {/* Logo area — always in banner; toggle inline */}
                      <div className={`cf-preview__logo${values.show_org_logo ? "" : " cf-preview__logo--add-mode"}`} onClick={e => e.stopPropagation()}>
                        {values.show_org_logo ? (
                          <>
                            {orgLogo
                              ? <img src={orgLogo} alt="org logo" className="cf-preview__logo-img" />
                              : <span className="cf-preview__logo-empty">No logo set</span>
                            }
                            <button type="button" className="cf-preview__logo-x" onClick={() => setFieldValue("show_org_logo", false)}>✕</button>
                          </>
                        ) : (
                          <button type="button" className="cf-preview__logo-add" onClick={() => setFieldValue("show_org_logo", true)}>
                            + Logo
                          </button>
                        )}
                      </div>

                      {/* Title + subtitle overlaid on banner when color-only (no image) */}
                      {values.banner_color && !values.cover_image_url && (
                        <div className="cf-preview__hero-title-area" onClick={e => e.stopPropagation()}>
                          {editingTitle ? (
                            <div>
                              <div className="cf-preview__title-edit-row cf-preview__title-edit-row--banner">
                                <input
                                  autoFocus
                                  className="cf-preview__title-field cf-preview__title-field--banner"
                                  value={values.title}
                                  placeholder="Campaign title…"
                                  maxLength={TITLE_MAX}
                                  onChange={e => setFieldValue("title", e.target.value)}
                                  onBlur={() => setEditingTitle(false)}
                                  onKeyDown={e => e.key === "Enter" && setEditingTitle(false)}
                                />
                                <button type="button" className="cf-preview__edit-btn cf-preview__edit-btn--banner cf-preview__edit-btn--done" onMouseDown={e => e.preventDefault()} onClick={() => setEditingTitle(false)}>
                                  <CheckIcon style={{ fontSize: 14 }} /> Done
                                </button>
                              </div>
                              {TITLE_MAX - (values.title?.length || 0) <= 20 && (
                                <span style={{ fontSize: 11, color: TITLE_MAX - (values.title?.length || 0) <= 5 ? "#e53935" : "rgba(255,255,255,0.7)" }}>
                                  {TITLE_MAX - (values.title?.length || 0)} characters left
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="cf-preview__title-view" onClick={() => setEditingTitle(true)}>
                              <span className="cf-preview__title-text cf-preview__title-text--banner">
                                {values.title || <em style={{ color: "rgba(255,255,255,0.5)" }}>Click to add title…</em>}
                              </span>
                              <button type="button" className="cf-preview__edit-btn cf-preview__edit-btn--banner">
                                <EditIcon style={{ fontSize: 14 }} /> Edit
                              </button>
                            </div>
                          )}
                          <div>
                            <input
                              className="cf-preview__subtitle-field cf-preview__subtitle-field--banner"
                              value={values.sub_title}
                              placeholder="Sub title (optional)…"
                              maxLength={SUBTITLE_MAX}
                              onChange={e => setFieldValue("sub_title", e.target.value)}
                            />
                            {SUBTITLE_MAX - (values.sub_title?.length || 0) <= 20 && (
                              <span style={{ fontSize: 11, color: SUBTITLE_MAX - (values.sub_title?.length || 0) <= 5 ? "#e53935" : "rgba(255,255,255,0.7)" }}>
                                {SUBTITLE_MAX - (values.sub_title?.length || 0)} characters left
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Title + Sub title — below banner (hidden when title is in the banner) */}
                  {!(values.show_banner && values.banner_color && !values.cover_image_url) && (
                  <div className="cf-preview__title-section">
                    {editingTitle ? (
                      <div>
                        <div className="cf-preview__title-edit-row">
                          <input
                            autoFocus
                            className="cf-preview__title-field"
                            value={values.title}
                            placeholder="Campaign title…"
                            maxLength={TITLE_MAX}
                            onChange={e => setFieldValue("title", e.target.value)}
                            onBlur={() => setEditingTitle(false)}
                            onKeyDown={e => e.key === "Enter" && setEditingTitle(false)}
                          />
                          <button type="button" className="cf-preview__edit-btn cf-preview__edit-btn--done" onMouseDown={e => e.preventDefault()} onClick={() => setEditingTitle(false)}>
                            <CheckIcon style={{ fontSize: 14 }} /> Done
                          </button>
                        </div>
                        {TITLE_MAX - (values.title?.length || 0) <= 20 && (
                          <span style={{ fontSize: 11, color: TITLE_MAX - (values.title?.length || 0) <= 5 ? "#e53935" : "#6b7280" }}>
                            {TITLE_MAX - (values.title?.length || 0)} characters left
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="cf-preview__title-view" onClick={() => setEditingTitle(true)}>
                        <span className="cf-preview__title-text">
                          {values.title || <em style={{ color: "#9ca3af" }}>Click to add title…</em>}
                        </span>
                        <button type="button" className="cf-preview__edit-btn">
                          <EditIcon style={{ fontSize: 14 }} /> Edit
                        </button>
                      </div>
                    )}
                    <div>
                      <input
                        className="cf-preview__subtitle-field"
                        value={values.sub_title}
                        placeholder="Sub title (optional)…"
                        maxLength={SUBTITLE_MAX}
                        onChange={e => setFieldValue("sub_title", e.target.value)}
                      />
                      {SUBTITLE_MAX - (values.sub_title?.length || 0) <= 20 && (
                        <span style={{ fontSize: 11, color: SUBTITLE_MAX - (values.sub_title?.length || 0) <= 5 ? "#e53935" : "#6b7280" }}>
                          {SUBTITLE_MAX - (values.sub_title?.length || 0)} characters left
                        </span>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Content: description + sidebar */}
                  <div className="cf-preview__body">

                    {/* Description */}
                    <div className="cf-preview__main">
                      <div className="cf-preview__section-head">
                        <span>About this campaign</span>
                        <button
                          type="button"
                          className={`cf-preview__edit-btn${editingDescription ? " cf-preview__edit-btn--done" : ""}`}
                          onClick={() => setEditingDescription(d => !d)}
                        >
                          {editingDescription
                            ? <><CheckIcon style={{ fontSize: 14 }} /> Done</>
                            : <><EditIcon style={{ fontSize: 14 }} /> Edit</>
                          }
                        </button>
                      </div>

                      {editingDescription ? (
                        <CampaignDescriptionEditor
                          key={`desc-${campaign?._id || "new"}-${open}`}
                          initialHtml={values.description}
                          onChange={html => setFieldValue("description", html)}
                        />
                      ) : (
                        <div
                          className="cf-preview__desc"
                          onClick={() => setEditingDescription(true)}
                          dangerouslySetInnerHTML={{
                            __html: values.description || "<p><em style='color:#9ca3af'>Click Edit to add a description…</em></p>",
                          }}
                        />
                      )}
                    </div>

                    {/* Sidebar */}
                    <div className="cf-preview__sidebar">
                      <div className="cf-preview__progress-wrap">
                        <div className="cf-preview__progress-bar">
                          <div className="cf-preview__progress-fill" style={{ width: "0%" }} />
                        </div>
                        <div className="cf-preview__goal-text">
                          {values.goal_amount
                            ? <>Goal: <strong>${Number(values.goal_amount).toLocaleString()}</strong></>
                            : <span style={{ color: "#9ca3af" }}>Set goal in Settings</span>
                          }
                        </div>
                      </div>

                      <div className="cf-preview__stats">
                        <div className="cf-preview__stat">
                          <span className="cf-preview__stat-val">$0</span>
                          <span className="cf-preview__stat-lbl">raised</span>
                        </div>
                        <div className="cf-preview__stat">
                          <span className="cf-preview__stat-val">0</span>
                          <span className="cf-preview__stat-lbl">donors</span>
                        </div>
                      </div>

                      {values.end_date && (
                        <div className="cf-preview__end-date">
                          Ends {formatDate({ date: values.end_date })}
                        </div>
                      )}

                      <button type="button" className="cf-preview__donate" disabled>
                        Donate Now
                      </button>

                      <div className="cf-preview__hint">
                        Preview only — live stats shown after launch
                      </div>
                    </div>
                  </div>
                </div>

              ) : (

                /* ══════════════════ SETTINGS TAB ══════════════════ */
                <div style={{ padding: "24px" }}>
                  <Grid container spacing={3}>

                    {/* Campaign Details */}
                    <Grid item xs={12}>
                      <span className={classes.sectionLabel}>Campaign Details</span>
                      <div className={classes.sectionBlock}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field as={SysTextField} name="slug" label="URL Slug (auto-generated if empty)"
                              error={touched.slug && !!errors.slug} helperText={touched.slug && errors.slug}
                              onChange={e => setFieldValue("slug", e.target.value.toLowerCase().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, ""))} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Field as={SysTextField} name="goal_amount" label="Goal Amount ($) *" type="number"
                              error={touched.goal_amount && !!errors.goal_amount} helperText={touched.goal_amount && errors.goal_amount} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              selected={values.end_date ? new Date(values.end_date) : null}
                              onChange={(date) => setFieldValue("end_date", date)}
                              dateFormat={getDateFormatForDatePicket()}
                              placeholderText={getDateFormat()}
                              customInput={<DateInput label="End Date" />}
                              isClearable
                            />
                          </Grid>
                          {isEdit && (
                            <Grid item xs={12} sm={6}>
                              <Field as={SysTextField} name="status" label="Status" select>
                                {["draft", "active", "paused", "completed", "archived"].map(s => (
                                  <MenuItem key={s} value={s} style={{ fontFamily: fontConfig.FundraisingMenuItemFont.family, fontSize: 14 }}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                  </MenuItem>
                                ))}
                              </Field>
                            </Grid>
                          )}
                        </Grid>
                      </div>
                    </Grid>

                    {/* Options */}
                    <Grid item xs={12}>
                      <span className={classes.sectionLabel}>Options</span>
                      <div className={classes.sectionBlock}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <FormControlLabel
                                control={<Checkbox checked={values.show_donor_wall} onChange={e => setFieldValue("show_donor_wall", e.target.checked)} color="primary" size="small" />}
                                label={<span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 13, color: "#002E5C" }}>Show donor wall</span>}
                              />
                              <InfoTooltip text="Publicly lists the names of people who donated to this campaign on the campaign page." useFixed placement="bottom" style={{ marginLeft: '0px', transform: 'translateY(-3px)' }} />
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <FormControlLabel
                                control={<Checkbox checked={values.allow_anonymous} onChange={e => setFieldValue("allow_anonymous", e.target.checked)} color="primary" size="small" />}
                                label={<span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 13, color: "#002E5C" }}>Allow anonymous donations</span>}
                              />
                              <InfoTooltip text="Lets a donor choose to give without their name being shown publicly (e.g. on the donor wall)." useFixed placement="bottom" style={{ marginLeft: '0px', transform: 'translateY(-3px)' }} />
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <FormControlLabel
                                control={<Checkbox checked={values.allow_bill_account} onChange={e => setFieldValue("allow_bill_account", e.target.checked)} color="primary" size="small" />}
                                label={<span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 13, color: "#002E5C" }}>Allow members to bill their account</span>}
                              />
                              <InfoTooltip text="Lets a logged-in member choose to have this donation billed to their account instead of paying immediately on the donation page." useFixed placement="bottom" style={{ marginLeft: '0px', transform: 'translateY(-3px)' }} />
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <FormControlLabel
                                control={<Checkbox checked={values.show_countdown} onChange={e => setFieldValue("show_countdown", e.target.checked)} color="primary" size="small" />}
                                label={<span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 13, color: "#002E5C" }}>Show countdown timer</span>}
                              />
                              <InfoTooltip text="Shows a countdown to the campaign's End Date on the public campaign page. Has no effect if no End Date is set." useFixed placement="bottom" style={{ marginLeft: '0px', transform: 'translateY(-3px)' }} />
                            </div>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <Field as={SysTextField} name="checkout_style" label="Donation checkout style" select>
                                <MenuItem value="steps" style={{ fontFamily: fontConfig.FundraisingMenuItemFont.family, fontSize: 14 }}>Step-by-step (3 screens)</MenuItem>
                                <MenuItem value="single" style={{ fontFamily: fontConfig.FundraisingMenuItemFont.family, fontSize: 14 }}>Single page</MenuItem>
                              </Field>
                              <InfoTooltip text="Step-by-step splits the donation flow across multiple screens (amount, details, payment). Single page shows all of them on one screen." useFixed placement="bottom" style={{ marginLeft: '8px', marginBottom: '8px' }} />
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    {/* Donation Amounts */}
                    <Grid item xs={12}>
                      <span className={classes.sectionLabel}>Donation Amounts</span>
                      <div className={classes.sectionBlock}>
                        <span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 12, color: "#002E5C", opacity: 0.5, display: "block", marginBottom: 10 }}>
                          Set the preset amount options donors can choose from.
                        </span>
                        <div className={classes.chipRow}>
                          {values.preset_amounts.map((amt, i) => (
                            <span key={i} className={classes.amountChip}>
                              ${amt}
                              <span
                                className={classes.removeChip}
                                onClick={() => setFieldValue("preset_amounts", values.preset_amounts.filter((_, j) => j !== i))}
                              >×</span>
                            </span>
                          ))}
                          <TextField
                            size="small" type="number" placeholder="Add amount"
                            value={newAmount}
                            onChange={e => {
                              const val = e.target.value;
                              setNewAmount(val === "" ? "" : String(Math.min(Math.max(0, Number(val)), 100000)));
                            }}
                            onWheel={e => e.target.blur()}
                            onKeyDown={e => {
                              if (e.key === "Enter" && newAmount && Number(newAmount) > 0) {
                                e.preventDefault();
                                setFieldValue("preset_amounts", [...values.preset_amounts, Number(newAmount)].sort((a, b) => a - b));
                                setNewAmount("");
                              }
                            }}
                            InputProps={{
                              style: { ...inputStyle, height: 32 },
                              endAdornment: (
                                <Tooltip title="Add (or press Enter)">
                                  <IconButton size="small" onClick={() => {
                                    if (newAmount && Number(newAmount) > 0) {
                                      setFieldValue("preset_amounts", [...values.preset_amounts, Number(newAmount)].sort((a, b) => a - b));
                                      setNewAmount("");
                                    }
                                  }}>
                                    <AddIcon fontSize="small" style={{ color: "#2555EF" }} />
                                  </IconButton>
                                </Tooltip>
                              ),
                            }}
                            style={{ width: 160 }}
                            variant="outlined"
                          />
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <FormControlLabel
                              control={<Checkbox checked={values.allow_open_amount} onChange={e => setFieldValue("allow_open_amount", e.target.checked)} color="primary" size="small" />}
                              label={<span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 13, color: "#002E5C" }}>Allow custom (open) amount</span>}
                            />
                            <InfoTooltip text="Lets donors type in their own amount instead of only picking from the preset amounts above." useFixed placement="bottom" style={{ marginLeft: '0px', transform: 'translateY(-3px)' }} />
                          </div>
                          {values.allow_open_amount && (
                            <div style={{ marginTop: 8, maxWidth: 200 }}>
                              <Field as={SysTextField} name="minimum_amount" label="Minimum donation ($)" type="number"
                                error={touched.minimum_amount && !!errors.minimum_amount} helperText={touched.minimum_amount && errors.minimum_amount}
                                inputProps={{ onWheel: e => e.target.blur() }} />
                            </div>
                          )}
                        </div>
                      </div>
                    </Grid>

                    {/* Finance Mapping */}
                    <Grid item xs={12} style={{ paddingBottom: 8 }}>
                      <span className={classes.sectionLabel}>Finance Mapping</span>
                      <div className={classes.sectionBlock}>
                        <span style={{ fontFamily: fontConfig.FundraisingCampaignFormTextFont.family, fontSize: 12, color: "#002E5C", opacity: 0.5, display: "block", marginBottom: 12 }}>
                          Donations to this campaign will be recorded under the selected ledger account.
                        </span>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <LabelField title="Ledger Account" />
                            <Select
                              classNamePrefix="react-select"
                              placeholder="Select"
                              options={ledgers?.map(l => ({ label: l.ledger_name, value: l._id, raw: l }))}
                              value={values.ledger?.id ? { label: values.ledger.name, value: values.ledger.id } : null}
                              onChange={opt => {
                                setFieldValue("ledger", { id: opt.value, name: opt.label });
                                setFieldValue("sub_ledger", { id: "", name: "" });
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="top"
                              onFocus={scrollSelectIntoView}
                              onMenuOpen={scrollSelectIntoView}
                              styles={selectMenuStyles}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <LabelField title="Sub-Ledger Account" />
                            <Select
                              classNamePrefix="react-select"
                              placeholder="Select"
                              isClearable
                              isDisabled={!values.ledger?.id}
                              options={ledgers?.find(l => l._id === values.ledger?.id)?.sub_ledgers?.map(s => ({ label: s.sub_ledger_name, value: s._id })) || []}
                              value={values.sub_ledger?.id ? { label: values.sub_ledger.name, value: values.sub_ledger.id } : null}
                              onChange={opt => setFieldValue("sub_ledger", opt ? { id: opt.value, name: opt.label } : { id: "", name: "" })}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="top"
                              onFocus={scrollSelectIntoView}
                              onMenuOpen={scrollSelectIntoView}
                              styles={selectMenuStyles}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                  </Grid>
                </div>
              )}
            </DialogContent>

            {!cropperOpen && (
            <div className={classes.dialogFooter}>
              <Button variant="primary-outline" size="md" onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="md" disabled={isSubmitting} type="button"
                onClick={() => handleClickSubmit(validateForm, submitForm, setFieldTouched)}>
                {isSubmitting && <CircularProgress size={14} style={{ marginRight: 6, color: "#fff" }} />}
                {isEdit ? "Save Changes" : "Create Campaign"}
              </Button>
            </div>
            )}

          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const LogoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

export default CampaignForm;
